import { useState } from 'react';
import { Button, ButtonGroup, Col, Row } from 'reactstrap';

import { default as Money } from 'Lib/components/common/Currency';
import CurrencyField from 'Lib/components/form/CurrencyField';
import Form from 'Lib/components/form/Form';
import isEmpty from 'Lib/utilities/isEmpty';

import { useFilter } from './useFilter';

const MODES = {
    FIXED: 'fixed',
    RANGE: 'range'
};

function Currency(props) {
    const { defaultMode = MODES.FIXED, placeholder = 'Enter an Amount', minPlaceholder = 'Min', maxPlaceholder = 'Max', minName = 'minAmount', maxName = 'maxAmount' } = props;
    const { context, options } = props;

    const [mode, setMode] = useState(defaultMode);

    const filter = useFilter(
        props,
        () => {
            const minAmount = context.getFilterValue(minName);
            const maxAmount = context.getFilterValue(maxName);
            let newMode = MODES.RANGE;

            if ((minAmount && isEmpty(maxAmount)) || minAmount == maxAmount) {
                newMode = MODES.FIXED;
            }

            setMode(newMode);
            bindValue(minAmount, maxAmount, newMode);
        },
        handleClear
    );

    const handleClear = form => {
        if (form) {
            form.setFieldValue('minAmount', null);
            form.setFieldValue('maxAmount', null);
        }

        setFilterValues([
            { name: minName, value: null },
            { name: maxName, value: null }
        ]);
        setValue(null);
    };

    const handleCancel = () => {
        handleToggle();
    };

    const { value, setValue, handleToggle, setFilterValues, renderMenuButtons } = filter;

    const handleModeChange = mode => {
        setMode(mode);
    };

    const handleSubmit = values => {
        const { minAmount, maxAmount } = values;
        const label = renderLabel(minAmount, maxAmount, mode);

        setValue({ value: { minAmount: minAmount, maxAmount: maxAmount }, label });
        setFilterValues([
            { name: minName, value: minAmount },
            { name: maxName, value: mode == MODES.FIXED ? minAmount : maxAmount }
        ]);
        handleToggle();
    };

    const bindValue = (minAmount, maxAmount, labelMode) => {
        if (isEmpty(minAmount) && isEmpty(maxAmount)) return;

        setValue({
            value: {
                minAmount,
                maxAmount
            },
            label: renderLabel(minAmount, maxAmount, labelMode)
        });
    };

    const renderLabel = (minAmount, maxAmount, labelMode) => {
        if (isEmpty(minAmount) && isEmpty(maxAmount)) return null;

        minAmount = parseFloat(minAmount);
        maxAmount = parseFloat(maxAmount);

        let label = null;

        if (labelMode == MODES.FIXED) {
            if (!isEmpty(minAmount)) {
                label = <Money value={minAmount} />;
                maxAmount = minAmount;
            }
        } else if (labelMode == MODES.RANGE) {
            if (minAmount > maxAmount) {
                const holder = minAmount;
                minAmount = maxAmount;
                maxAmount = holder;
            }

            if (minAmount && isEmpty(maxAmount)) {
                label = (
                    <>
                        &gt;= <Money value={minAmount} />
                    </>
                );
            } else if (maxAmount && isEmpty(minAmount)) {
                label = (
                    <>
                        &lt;= <Money value={maxAmount} />
                    </>
                );
            } else if (minAmount != null && maxAmount) {
                label = (
                    <>
                        <Money value={minAmount} /> - <Money value={maxAmount} />
                    </>
                );
            }
        }

        return label;
    };

    return filter.render(
        <div
            className="searchable-list-filter-menu"
            style={{
                padding: '10px',
                minWidth: '300px'
            }}
            key={mode}
        >
            <Form
                initialValues={{
                    minAmount: value?.value?.minAmount,
                    maxAmount: value?.value?.maxAmount
                }}
                onSubmit={handleSubmit}
                render={form => {
                    return (
                        <form onSubmit={form.handleSubmit}>
                            {mode == 'fixed' ? <CurrencyField placeholder={placeholder} name="minAmount" label={props.label} hideLabel={true} options={options} /> : null}
                            {mode == 'range' ? (
                                <Row className="form-row">
                                    <Col md="5">
                                        <CurrencyField placeholder={minPlaceholder} name="minAmount" label={props.label} hideLabel={true} options={options} />
                                    </Col>
                                    <Col md="2">
                                        <div
                                            style={{
                                                textAlign: 'center',
                                                paddingTop: '1rem'
                                            }}
                                        >
                                            to
                                        </div>
                                    </Col>
                                    <Col md="5">
                                        <CurrencyField placeholder={maxPlaceholder} name={'maxAmount'} label={props.label} hideLabel={true} options={options} />
                                    </Col>
                                </Row>
                            ) : null}
                            <div
                                style={{
                                    textAlign: 'center',
                                    marginBottom: '1rem'
                                }}
                            >
                                <ButtonGroup size="sm">
                                    <Button
                                        color={mode == MODES.FIXED ? 'info' : 'secondary'}
                                        outline={mode == MODES.FIXED ? false : true}
                                        onClick={() => handleModeChange(MODES.FIXED)}
                                    >
                                        Fixed
                                    </Button>
                                    <Button
                                        color={mode == MODES.RANGE ? 'info' : 'secondary'}
                                        outline={mode == MODES.RANGE ? false : true}
                                        onClick={() => handleModeChange(MODES.RANGE)}
                                    >
                                        Range
                                    </Button>
                                </ButtonGroup>
                            </div>
                            {renderMenuButtons({ handleCancel, handleClear, form })}
                        </form>
                    );
                }}
            />
        </div>
    );
}

export default Currency;
